import { Component, OnChanges, OnInit, SimpleChanges, inject, ChangeDetectionStrategy, signal, output, input } from '@angular/core';
import {SureFlowLoopDetail} from '../../model/sure-flow-loop-detail';
import {SureFlowUnitData} from '../../model/sure-flow-unit-data';
import {UnitSelectionService} from '../../services/unit-selection-service';
import {StringUtils} from '../../utils/string-utils';
import {Observable, Subject} from 'rxjs';
import {SelectionItem} from '../../selections/selection-item';
import {UnitConversionUtils} from '../../configurator/unit-conversion-utils';
import {MotorType} from '../../model/motor-type';
import {RatingsModel} from '../../model/ratings-model';
import {ModelService} from '../../services/model-service';
import {RatingService} from '../../services/rating.service';
import {SelectionModelSizeCharacteristic} from '../../model/selection-model-size-characteristic';
import {WorkingSureFlowUnit} from '../../model/working-sure-flow-unit';
import {SelectionSystemType} from '../../model/selection-system-type';
import {SureFlowRatingUnit} from '../../model/sure-flow-rating-unit';
import {SureFlowRatingCoilRequest} from '../../model/sure-flow-rating-coil-request';
import {SureFlowRatingUnitRequest} from '../../model/sure-flow-rating-unit-request';
import {RatingUnitUtils} from '../../utils/rating-unit-utils';
import {SureFlowRatingUnitResponse} from '../../model/sure-flow-rating-unit-response';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { SureFlowPerformanceOutputComponent } from '../sure-flow-performance-output/sure-flow-performance-output.component';
import { SureFlowPerformanceInputComponent } from '../sure-flow-performance-input/sure-flow-performance-input.component';
import { FormsModule } from '@angular/forms';


@Component({
    selector: 'app-sure-flow-rating-tool',
    templateUrl: './sure-flow-rating-tool.component.html',
    styleUrls: ['./sure-flow-rating-tool.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, SureFlowPerformanceInputComponent, SureFlowPerformanceOutputComponent, MatProgressSpinner]
})
export class SureFlowRatingToolComponent implements OnInit, OnChanges {
  private readonly unitSelectionService = inject(UnitSelectionService);
  private readonly modelService = inject(ModelService);
  private readonly ratingService = inject(RatingService);


  readonly loopInput = input.required<SureFlowLoopDetail>({ alias: 'loop' });
  readonly unitDataInput = input.required<SureFlowUnitData>({ alias: 'unitData' });
  readonly ratingUnitInput = input.required<SureFlowRatingUnit>({ alias: 'ratingUnit' });
  readonly performanceChanged = output<WorkingSureFlowUnit>();
  readonly productIds = signal<string[]>(undefined!);
  readonly allModels = signal<RatingsModel[]>(undefined!);
  readonly availableModels = signal<RatingsModel[]>(undefined!);
  readonly systemTypes = signal<SelectionSystemType[]>(undefined!);
  readonly coilRows = signal<SelectionItem[]>(undefined!);
  readonly availableSizes = signal<SelectionModelSizeCharacteristic[]>(undefined!);
  readonly availableMotorTypes = signal<MotorType[]>(undefined!);
  readonly availableMotorVoltages = signal<SelectionItem[]>(undefined!);
  readonly availableElectricHeatKw = signal<SelectionItem[]>(undefined!);
  readonly availableFilters = signal<SelectionItem[]>(undefined!);
  readonly showCoolingInputs = signal<boolean>(false);
  readonly showHeatingInputs = signal<boolean>(false);
  readonly showHydronicHeating = signal<boolean>(false);
  readonly showElectricHeating = signal<boolean>(false);
  readonly initialLoadComplete = signal<boolean>(false);
  readonly isAirHandlerModel = signal<boolean>(undefined!);
  readonly engineKey = signal<string>(undefined!);
  readonly ratingsError = signal<string>(undefined!);
  readonly isBusy = signal<boolean>(undefined!);
  readonly errorMessage = signal<string>(undefined!);

  ngOnInit(): void {
    this.unitSelectionService.getSureFlowModels().subscribe({
      next: (models) => {
        this.allModels.set(models);
        this.productIds.set([...new Set(models.map(model => model.productId))]);
        this.productIdChanged();
      }, error: (error) => this.handleError(error)
    });
  }

  private handleError(error: any): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  ngOnChanges(changes: SimpleChanges): void {
  }

  productIdChanged(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.initialLoadComplete.set(false);
    this.handleProductChange().subscribe({
      complete: () => {
        this.handleModelChange().subscribe({
          complete: () => {
            this.initialLoadComplete.set(true);
            this.updateUnitRating().subscribe({
              complete: () => this.isBusy.set(false),
              error: (error) => this.handleError(error)
            });
          },
          error: (error) => this.handleError(error)
        });
      }, error: (error) => this.handleError(error)
    });
  }

  emitChanges(): void {
    const unit = new WorkingSureFlowUnit();
    unit.unitData = this.unitDataInput();
    unit.unitData.sureFlowRatingUnit = this.ratingUnitInput();
    this.performanceChanged.emit(unit);
  }

  private handleProductChange(): Observable<void> {
    this.availableModels.set([]);
    this.clearResponseValues();
    const subject: Subject<void> = new Subject<void>();
    if (this.ratingUnitInput().productId && this.ratingUnitInput().productId.length > 0) {
      this.availableModels.set(this.allModels().filter(model => model.productId === this.ratingUnitInput().productId));
      if (this.ratingUnitInput().modelCode) {
        const filteredModels = this.availableModels().filter(model => model.modelCode === this.ratingUnitInput().modelCode);
        if (filteredModels.length !== 1) {
          if (this.ratingUnitInput().request) {
            this.ratingUnitInput().request.model = undefined!;
          }
          this.ratingUnitInput().modelCode = undefined!;
        }
      }
      this.modelService.getEngineKey(this.ratingUnitInput().productId).subscribe({
        next: (key) => {
          this.engineKey.set(key);
          this.loadSystemTypes().subscribe({
            next: () => subject.next(),
            complete: () => subject.complete(),
            error: (error) => subject.error(error),
          });
        },
        error: (error) => subject.error(error)
      });
    } else {
      subject.complete();
    }
    return subject.asObservable();
  }

  private loadSystemTypes(): Observable<void> {
    const subject: Subject<void> = new Subject<void>();
    this.coilRows.set([]);
    this.systemTypes.set([]);
    this.unitSelectionService.getSureFlowSystemTypes().subscribe({
      next: (systemTypes) => {
        this.systemTypes.set(systemTypes);
        this.handleSystemTypeChange().subscribe({
          next: () => subject.next(),
          error: (error) => subject.error(error),
          complete: () => subject.complete()
        });
      }, error: (error) => subject.error(error)
    });
    return subject.asObservable();
  }

  private handleSystemTypeChange(): Observable<void> {
    const subject: Subject<void> = new Subject<void>();
    this.showCoolingInputs.set(false);
    this.showHydronicHeating.set(false);
    this.showHeatingInputs.set(false);
    if (this.loopInput().systemTypeCode?.length || 0 > 0) {
      const filtered = this.systemTypes().filter(systemType => systemType.code === this.loopInput().systemTypeCode);
      if (filtered.length === 1) {
        const systemType = filtered[0];
        this.modelService.setValue('USSystemType', this.engineKey(), systemType.code).subscribe({
          next: () => {
            this.modelService.isComplete(this.engineKey()).subscribe({
              next: () => {
                this.showCoolingInputs.set(StringUtils.equalsIgnoreCase(systemType.primaryCoilApplication, 'C'));
                this.showHydronicHeating.set(StringUtils.equalsIgnoreCase(systemType.primaryCoilApplication, 'H') || StringUtils.equalsIgnoreCase(systemType.secondaryCoilApplication, 'H'));
                this.showElectricHeating.set(systemType.electricHeat);
                this.showHeatingInputs.set(this.showHydronicHeating() || this.showElectricHeating());
                if (this.showCoolingInputs()) {
                  this.ratingUnitInput().request.primaryCoilRequest = this.buildDefaultCoolingRequest();
                  if (this.showHydronicHeating()) {
                    this.ratingUnitInput().request.secondaryCoilRequest = this.buildDefaultHeatingRequest();
                  } else {
                    this.ratingUnitInput().request.secondaryCoilRequest = undefined!;
                  }
                } else if (this.showHydronicHeating()) {
                  this.ratingUnitInput().request.primaryCoilRequest = this.buildDefaultHeatingRequest();
                  this.ratingUnitInput().request.secondaryCoilRequest = undefined!;
                }
                if (this.showElectricHeating()) {
                  this.ratingUnitInput().request.electricHeatRequest = this.buildDefaultElectricHeatRequest();
                  this.setElectricHeatVoltage();
                }
                this.loadElectricHeatKw().subscribe({
                  complete: () => {
                    this.handleElectricHeatKwChange().subscribe({
                      complete: () => {
                        this.loadCoilRows().subscribe({
                          complete: () => {
                            if (this.coilRows().length === 0) {
                              this.ratingUnitInput().coilRowCode = undefined!;
                              if (this.ratingUnitInput().request.primaryCoilRequest) {
                                this.ratingUnitInput().request.primaryCoilRequest.rowCount = 0;
                              }
                              if (this.ratingUnitInput().request.secondaryCoilRequest) {
                                this.ratingUnitInput().request.secondaryCoilRequest.rowCount = 0;
                              }
                              this.clearResponseValues();
                            }
                            this.handleCoilRowsChange().subscribe({
                              complete: () => {
                                this.updateUnitRating().subscribe({
                                  next: () => subject.complete(),
                                  error: (error) => subject.error(error),
                                  complete: () => subject.complete()
                                });
                              }, error: (error) => subject.error(error)
                            });
                          }, error: (error) => subject.error(error)
                        });
                      }, error: (error) => subject.error(error)
                    });
                  }, error: (error) => subject.error(error)
                });
              }, error: (error) => subject.error(error)
            });
          }, error: (error) => subject.error(error)
        });
      } else {
        this.ratingUnitInput().request = this.initialRatingRequest();
        subject.complete();
      }
    } else {
      subject.complete();
    }
    return subject.asObservable();
  }

  private updateUnitRating(): Observable<void> {
    this.clearResponseValues();
    const subject: Subject<void> = new Subject<void>();
    if (this.initialLoadComplete()) {
      if (this.isValidRequest()) {
        const request = RatingUnitUtils.convertSureFlowRatingRequest(this.ratingUnitInput().request);
        this.ratingService.getUnitRatingResponse(request).subscribe({
          next: (response) => {
            this.setResponseValues(RatingUnitUtils.convertRatingResponse(response));
            if (response.ErrorMessage) {
              this.ratingsError.set(response.ErrorMessage);
            }
            this.emitChanges();
            subject.complete();
          },
          error: (error) => subject.error(error),
          complete: () => subject.complete()
        });
      } else {
        subject.complete();
      }
    } else {
      subject.complete();
    }
    return subject.asObservable();
  }

  private clearResponseValues(): void {
    this.ratingUnitInput().response = undefined!;
  }

  private isValidRequest(): boolean {
    if (!this.ratingUnitInput().request) {
      return false;
    }
    if (!this.ratingUnitInput().modelCode) {
      return false;
    }
    if (!this.ratingUnitInput().sizeCode) {
      return false;
    }
    if (!this.loopInput().systemTypeCode) {
      return false;
    }
    if (!this.ratingUnitInput().coilRowCode) {
      return false;
    }
    if (!this.ratingUnitInput().powerSupplyCode) {
      return false;
    }
    if (!this.ratingUnitInput().motorTypeCode) {
      return false;
    }
    if ((this.availableFilters()?.length || 0) > 0 && !this.ratingUnitInput().request.filter) {
      return false;
    }
    if (this.showElectricHeating() && !this.ratingUnitInput().electricHeatCode) {
      return false;
    }
    if (this.showCoolingInputs()) {
      if (this.isZero(this.unitDataInput().coolingTargetTotalLoad)) {
        return false;
      }
      if (this.isZero(this.ratingUnitInput().request.primaryCoilRequest?.enteringDryBulb)) {
        return false;
      }
      if (this.isZero(this.ratingUnitInput().request.primaryCoilRequest?.enteringWetBulb)) {
        return false;
      }
      if (this.isZero(this.ratingUnitInput().request.primaryCoilRequest?.enteringFluidTemperature)) {
        return false;
      }
      if (this.showHeatingInputs()) {
        if (this.showElectricHeating()) {
          if (this.isZero(this.ratingUnitInput().request.electricHeatRequest?.enteringDryBulb)) {
            return false;
          }
        }
        if (this.showHydronicHeating()) {
          if (this.isZero(this.unitDataInput().heatingTargetTotalLoad)) {
            return false;
          }
          if (this.isZero(this.ratingUnitInput().request.secondaryCoilRequest?.enteringDryBulb)) {
            return false;
          }
          if (this.isZero(this.ratingUnitInput().request.secondaryCoilRequest?.enteringFluidTemperature)) {
            return false;
          }
        }
      }
    } else if (this.showHeatingInputs()) {
      if (this.showElectricHeating()) {
        if (this.isZero(this.ratingUnitInput().request.electricHeatRequest?.enteringDryBulb)) {
          return false;
        }
      }
      if (this.showHydronicHeating()) {
        if (this.isZero(this.unitDataInput().heatingTargetTotalLoad)) {
          return false;
        }
        if (this.isZero(this.ratingUnitInput().request.primaryCoilRequest?.enteringDryBulb)) {
          return false;
        }
        if (this.showHydronicHeating()) {
          if (this.isZero(this.ratingUnitInput().request.primaryCoilRequest?.enteringFluidTemperature)) {
            return false;
          }
        }
      }
    }
    return true;
  }

  private isZero(input: number): boolean {
    return isNaN(input) || (input || 0) === 0;
  }

  private setElectricHeatVoltage(): void {
    if (this.ratingUnitInput().powerSupplyCode && this.availableMotorVoltages()?.length > 0) {
      if (this.ratingUnitInput().request?.electricHeatRequest) {
        const filteredVoltage = this.availableMotorVoltages().filter(voltage => this.ratingUnitInput().powerSupplyCode === voltage.code);
        if (filteredVoltage.length === 1) {
          this.ratingUnitInput().request.electricHeatRequest.electricHeatVoltage = filteredVoltage[0].description;
        }
      }
    }
  }

  private loadCoilRows(): Observable<void> {
    this.coilRows.set([]);
    const subject = new Subject<void>();
    this.modelService.getEntity(this.engineKey(), 'USCoilRows').subscribe({
      next: (coilRowEntity) => {
        for (const allowable of coilRowEntity.Allowables) {
          if (allowable.IsValid) {
            const coilRow: SelectionItem = new SelectionItem();
            coilRow.code = allowable.Value;
            coilRow.description = allowable.Description;
            this.coilRows.update(current => [...current, coilRow]);
          }
        }
        subject.complete();
      }, error: (error) => subject.error(error)
    });
    return subject.asObservable();
  }

  private loadElectricHeatKw(): Observable<void> {
    const subject = new Subject<void>();
    this.availableElectricHeatKw.set([]);
    if (this.showElectricHeating()) {
      this.modelService.getEntity(this.engineKey(), 'USElecHtKw').subscribe({
        next: (electricHeat) => {
          for (const allowable of electricHeat.Allowables) {
            if (allowable.IsValid) {
              const heatKw = new SelectionItem();
              heatKw.code = allowable.Value;
              heatKw.description = allowable.Description;
              this.availableElectricHeatKw.update(current => [...current, heatKw]);
            }
          }
          subject.complete();
        }, error: (error) => subject.error(error)
      });
    } else {
      this.ratingUnitInput().request.electricHeatRequest = undefined!;
      this.ratingUnitInput().electricHeatCode = 'None';
      subject.complete();
    }
    return subject.asObservable();
  }

  electricHeatKwChanged(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.handleElectricHeatKwChange().subscribe({
      complete: () => {
        this.updateUnitRating().subscribe({
          next: () => this.isBusy.set(false),
          complete: () => this.isBusy.set(false),
          error: (error) => this.handleError(error)
        });
      }, error: (error) => this.handleError(error)
    });
  }

  private handleElectricHeatKwChange(): Observable<void> {
    const subject = new Subject<void>();
    if (this.ratingUnitInput().electricHeatCode && (this.availableElectricHeatKw()?.length || 0) > 0) {
      const filtered = this.availableElectricHeatKw().filter(row => row.code === this.ratingUnitInput().electricHeatCode);
      if (filtered.length !== 1) {
        this.ratingUnitInput().electricHeatCode = undefined!;
        subject.complete();
      } else {
        if (StringUtils.isEmptyNumericValue(+this.ratingUnitInput().electricHeatCode)) {
          this.ratingUnitInput().request.electricHeatRequest = undefined!;
        } else {
          if (!this.ratingUnitInput().request.electricHeatRequest) {
            this.ratingUnitInput().request.electricHeatRequest = this.buildDefaultElectricHeatRequest();
          }
          this.setElectricHeatVoltage();
          this.ratingUnitInput().request.electricHeatRequest.electricHeatKilowatts = (+this.ratingUnitInput().electricHeatCode) / 1000;
        }
        subject.complete();
      }
    } else {
      subject.complete();
    }
    return subject.asObservable();
  }

  coilRowsChanged(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.handleCoilRowsChange().subscribe({
      complete: () => {
        this.updateUnitRating().subscribe({
          next: () => this.isBusy.set(false),
          complete: () => this.isBusy.set(false),
          error: (error) => this.handleError(error)
        });
      }, error: (error) => this.handleError(error)
    });
  }

  private handleCoilRowsChange(): Observable<void> {
    const subject = new Subject<void>();
    if ((this.coilRows()?.length || 0) > 0) {
      const filtered = this.coilRows().filter(row => row.code === this.ratingUnitInput().coilRowCode);
      if (filtered.length !== 1) {
        this.ratingUnitInput().coilRowCode = undefined!;
        subject.complete();
      } else {
        this.modelService.setValue('USCoilRows', this.engineKey(), this.ratingUnitInput().coilRowCode).subscribe({
          next: () => {
            this.modelService.isComplete(this.engineKey()).subscribe({
              next: () => {
                this.modelService.getCharacteristicValue(this.engineKey(), 'USCoilRows', 'PriRows').subscribe({
                  next: (primaryRowCount) => {
                    this.modelService.getCharacteristicValue(this.engineKey(), 'USCoilRows', 'SecRows').subscribe({
                      next: (secondaryRowCount) => {
                        if (!isNaN(+primaryRowCount)) {
                          if (this.ratingUnitInput().request.primaryCoilRequest) {
                            this.ratingUnitInput().request.primaryCoilRequest.rowCount = +primaryRowCount;
                          }
                        }
                        if (!isNaN(+secondaryRowCount)) {
                          if (this.ratingUnitInput().request.secondaryCoilRequest) {
                            this.ratingUnitInput().request.secondaryCoilRequest.rowCount = +secondaryRowCount;
                          }
                        }
                        subject.complete();
                      }, error: (error) => subject.error(error),
                      complete: () => subject.complete()
                    });
                  }, error: (error) => subject.error(error),
                });
              }, error: (error) => subject.error(error)
            });
          }, error: (error) => subject.error(error)
        });
      }
    } else {
      this.ratingUnitInput().coilRowCode = undefined!;
      subject.complete();
    }
    return subject.asObservable();
  }

  private initialRatingRequest(): SureFlowRatingUnitRequest {
    const request = new SureFlowRatingUnitRequest();
    request.model = this.ratingUnitInput().modelCode;
    request.size = this.ratingUnitInput().sizeCode;
    if (this.isAirHandlerModel()) {
      request.fanSpeed = undefined!;
    } else {
      request.fanSpeed = 'H';
    }
    request.externalStaticPressure = 0;
    request.altitude = 0;
    request.metric = this.loopInput().metric;
    return request;
  }

  private buildDefaultElectricHeatRequest(): SureFlowRatingCoilRequest {
    const request = new SureFlowRatingCoilRequest();
    let dryBulb: number = this.loopInput().metric ? this.round(UnitConversionUtils.convertTemperatureValue(70, true)) : 70;
    let id: number = 0;
    if (this.ratingUnitInput() && this.ratingUnitInput().request && this.ratingUnitInput().request.electricHeatRequest) {
      dryBulb = this.ratingUnitInput().request.electricHeatRequest.enteringDryBulb;
      id = this.ratingUnitInput().request.electricHeatRequest.id;
    }
    request.id = id;
    request.enteringDryBulb = dryBulb;
    request.isElectricHeat = true;
    return request;
  }

  private round(value: string): number {
    const numericValue = +value;
    const roundedValue = numericValue.toFixed(3);
    return +roundedValue;
  }

  private buildDefaultCoolingRequest(): SureFlowRatingCoilRequest {
    try {
      const isMetric = this.loopInput().metric || false;
      const request = new SureFlowRatingCoilRequest();
      let dryBulb: number = isMetric ? this.round(UnitConversionUtils.convertTemperatureValue(75, true)) : 75;
      let wetBulb: number = isMetric ? this.round(UnitConversionUtils.convertTemperatureValue(63, true)) : 63;
      let enteringFluidTemp: number = isMetric ? this.round(UnitConversionUtils.convertTemperatureValue(45, true)) : 45;
      let rowCount: number = (this.ratingUnitInput().request?.primaryCoilRequest?.rowCount || 0);
      let id: number = 0;
      if (this.ratingUnitInput()) {
        const coolingRequest: SureFlowRatingCoilRequest = this.getSelectedCoolingRequest(this.ratingUnitInput());
        if (coolingRequest) {
          dryBulb = coolingRequest.enteringDryBulb;
          wetBulb = coolingRequest.enteringWetBulb;
          enteringFluidTemp = coolingRequest.enteringFluidTemperature || enteringFluidTemp;
          rowCount = coolingRequest.rowCount;
          id = coolingRequest.id;
        }
      }
      request.id = id;
      request.enteringDryBulb = dryBulb;
      request.enteringWetBulb = wetBulb;
      request.enteringFluidTemperature = enteringFluidTemp;
      request.fluidType = this.loopInput().coolingFluidTypeCode;
      request.glycolPercentage = this.loopInput().coolingGlycolPercentage;
      request.isCooling = true;
      request.rowCount = rowCount;
      return request;
    } catch (error) {
      this.handleError(error);
      return undefined!;
    }
  }

  private getSelectedCoolingRequest(unit: SureFlowRatingUnit): SureFlowRatingCoilRequest {
    if (unit && unit.request && unit.request.primaryCoilRequest) {
      if (!unit.request.primaryCoilRequest.enteringFluidTemperature) {
        return undefined!;
      } else {
        if (unit.request.primaryCoilRequest.enteringFluidTemperature < unit.request.primaryCoilRequest.enteringDryBulb) {
          return unit.request.primaryCoilRequest;
        } else {
          return undefined!;
        }
      }
    }
    return undefined!;
  }

  private buildDefaultHeatingRequest(): SureFlowRatingCoilRequest {
    try {
      const request = new SureFlowRatingCoilRequest();
      const isMetric = this.loopInput().metric;
      let dryBulb = isMetric ? this.round(UnitConversionUtils.convertTemperatureValue(70, true)) : 70;
      let fluidTemperature = isMetric ? this.round(UnitConversionUtils.convertTemperatureValue(180, true)) : 180;
      let rowCount = (this.ratingUnitInput().request?.primaryCoilRequest?.rowCount || 0);
      let id: number = 0;
      if (this.ratingUnitInput()) {
        const heatRequest = this.getSelectedHeatingRequest(this.ratingUnitInput());
        if (heatRequest) {
          dryBulb = heatRequest.enteringDryBulb;
          fluidTemperature = heatRequest.enteringFluidTemperature || fluidTemperature;
          rowCount = heatRequest.rowCount;
          id = heatRequest.id;
        }
      }
      request.id = id;
      request.enteringDryBulb = dryBulb;
      request.enteringFluidTemperature = fluidTemperature;
      request.fluidType = this.loopInput().heatingFluidTypeCode;
      request.glycolPercentage = this.loopInput().heatingGlycolPercentage;
      request.rowCount = rowCount;
      request.isCooling = false;
      request.isElectricHeat = false;
      return request;
    } catch (error) {
      this.handleError(error);
      return undefined!;
    }
  }

  private getSelectedHeatingRequest(unit: SureFlowRatingUnit): SureFlowRatingCoilRequest {
    if (unit && unit.request) {
      if (unit.request.secondaryCoilRequest) {
        if (!unit.request.secondaryCoilRequest.enteringFluidTemperature) {
          return undefined!;
        } else {
          if (unit.request.secondaryCoilRequest.enteringFluidTemperature > unit.request.secondaryCoilRequest.enteringDryBulb) {
            return unit.request.secondaryCoilRequest;
          } else {
            return undefined!;
          }
        }
      } else if (unit.request.primaryCoilRequest) {
        if (!unit.request.primaryCoilRequest.enteringFluidTemperature) {
          return undefined!;
        } else {
          if (unit.request.primaryCoilRequest.enteringFluidTemperature > unit.request.primaryCoilRequest.enteringDryBulb) {
            return unit.request.primaryCoilRequest;
          } else {
            return undefined!;
          }
        }
      } else {
        return undefined!;
      }
    }
    return undefined!;
  }

  selectedModelChanged(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.handleModelChange().subscribe({
      complete: () => {
        this.updateUnitRating().subscribe({
          complete: () => this.isBusy.set(false),
          error: (error) => this.handleError(error),
          next: () => this.isBusy.set(false)
        });
      }, error: (error) => this.handleError(error)
    });
  }

  private handleModelChange(): Observable<void> {
    const subject: Subject<void> = new Subject<void>();
    const restoreLoadedFlag = this.initialLoadComplete();
    this.isAirHandlerModel.set(false);
    this.initialLoadComplete.set(false);
    this.availableSizes.set([]);
    this.availableMotorTypes.set([]);
    this.availableMotorVoltages.set([]);
    if ((this.ratingUnitInput().modelCode?.trim()?.length || 0) > 0) {
      const filtered = this.allModels().filter(model => model.modelCode === this.ratingUnitInput().modelCode);
      if (filtered.length === 1) {
        this.ratingUnitInput().request.model = this.ratingUnitInput().modelCode;
        this.isAirHandlerModel.set(filtered[0].airHandlerModel);
        if (this.isAirHandlerModel() && this.ratingUnitInput().request) {
          this.ratingUnitInput().request.fanSpeed = undefined!;
        }
        this.modelService.setValue('USUnit', this.engineKey(), this.ratingUnitInput().modelCode).subscribe({
          next: () => {
            this.modelService.isComplete(this.engineKey()).subscribe({
              next: () => {
                this.unitSelectionService.getModelSizeCharacteristics(this.engineKey()).subscribe({
                  next: (characteristics) => {
                    this.availableSizes.set(characteristics);
                    this.handleSizeChange().subscribe({
                      complete: () => {
                        this.unitSelectionService.getEngineMotorVoltages(this.engineKey()).subscribe({
                          next: (voltages) => {
                            this.availableMotorVoltages.set(voltages);
                            this.handleSelectedMotorVoltageChange().subscribe({
                              complete: () => {
                                this.loadFilterTypes().subscribe({
                                  complete: () => {
                                    this.handleFilterTypeChange().subscribe({
                                      complete: () => {
                                        this.initialLoadComplete.set(restoreLoadedFlag);
                                        subject.complete();
                                      },
                                      error: (error) => {
                                        this.initialLoadComplete.set(restoreLoadedFlag);
                                        subject.error(error);
                                      }
                                    });
                                  },
                                  error: (error) => {
                                    this.initialLoadComplete.set(restoreLoadedFlag);
                                    subject.error(error);
                                  }
                                });
                              },
                              error: (error) => {
                                this.initialLoadComplete.set(restoreLoadedFlag);
                                subject.error(error);
                              }
                            });
                          },
                          error: (error) => {
                            this.initialLoadComplete.set(restoreLoadedFlag);
                            subject.error(error);
                          }
                        });
                      },
                      error: (error) => {
                        this.initialLoadComplete.set(restoreLoadedFlag);
                        subject.error(error);
                      }
                    });
                  },
                  error: (error) => {
                    this.initialLoadComplete.set(restoreLoadedFlag);
                    subject.error(error);
                  }
                });
              },
              error: (error) => {
                this.initialLoadComplete.set(restoreLoadedFlag);
                subject.error(error);
              }
            });
          },
          error: (error) => {
            this.initialLoadComplete.set(restoreLoadedFlag);
            subject.error(error);
          }
        });
      } else {
        this.initialLoadComplete.set(restoreLoadedFlag);
        this.ratingUnitInput().modelCode = undefined!;
        subject.complete();
      }
    } else {
      this.initialLoadComplete.set(restoreLoadedFlag);
      subject.complete();
    }
    return subject.asObservable();
  }

  getModelImageFileName(): string {
    if ((this.allModels()?.length || 0) > 0) {
      const selectedModelCode = (this.ratingUnitInput().modelCode || '');
      const filtered = this.allModels().filter(model => model.modelCode === selectedModelCode);
      if (filtered.length === 1) {
        return filtered[0].imageFileName;
      }
    }
    return '';
  }

  selectedMotorVoltageChanged(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.availableMotorTypes.set([]);
    this.handleSelectedMotorVoltageChange().subscribe({
      complete: () => {
        this.updateUnitRating().subscribe({
          next: () => this.isBusy.set(false),
          error: (error) => this.handleError(error),
          complete: () => this.isBusy.set(false)
        });
      }, error: (error) => this.handleError(error)
    });
  }

  private handleSelectedMotorVoltageChange(): Observable<void> {
    const subject = new Subject<void>();
    this.ratingUnitInput().powerSupplyCode = (this.ratingUnitInput()?.powerSupplyCode?.replace(/\//g, '') || undefined)!;
    if ((this.availableMotorVoltages()?.length || 0) > 0) {
      const filtered = this.availableMotorVoltages().filter(voltage => voltage.code === this.ratingUnitInput().powerSupplyCode);
      if (filtered.length !== 1) {
        this.ratingUnitInput().powerSupplyCode = undefined!;
        if (this.ratingUnitInput().request?.electricHeatRequest) {
          this.ratingUnitInput().request.electricHeatRequest.electricHeatVoltage = undefined!;
        }
        subject.complete();
      } else {
        this.setElectricHeatVoltage();
        this.modelService.setValue('USUnitVoltage', this.engineKey(), this.ratingUnitInput().powerSupplyCode).subscribe({
          next: () => {
            this.modelService.isComplete(this.engineKey()).subscribe({
              next: () => {
                this.modelService.getCharacteristicValue(this.engineKey(), 'USUnitVoltage', 'Description').subscribe({
                  next: (code) => {
                    this.ratingUnitInput().request.motorVoltage = code;
                    if (this.ratingUnitInput().request?.electricHeatRequest) {
                      this.ratingUnitInput().request.electricHeatRequest.electricHeatVoltage = code;
                    }
                    this.unitSelectionService.getEngineMotorTypes(this.engineKey()).subscribe({
                      next: (motorTypes: MotorType[]) => {
                        this.availableMotorTypes.set(motorTypes);
                        this.handleSelectedMotorTypeChange().subscribe({
                          complete: () => {
                            subject.complete();
                          }, error: (error) => subject.error(error)
                        });
                      }, error: (error) => subject.error(error)
                    });
                  }, error: (error) => subject.error(error)
                });
              }, error: (error) => subject.error(error)
            });
          }, error: (error) => subject.error(error)
        });
      }
    } else {
      subject.complete();
    }
    return subject.asObservable();
  }

  filterTypeChanged(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.handleFilterTypeChange().subscribe({
      complete: () => {
        this.updateUnitRating().subscribe({
          next: () => this.isBusy.set(false),
          complete: () => this.isBusy.set(false),
          error: (error) => this.handleError(error)
        });
      }, error: (error) => this.handleError(error)
    });
  }

  private handleFilterTypeChange(): Observable<void> {
    const subject = new Subject<void>();
    if (this.ratingUnitInput().request?.filter && (this.availableFilters()?.length || 0) > 0) {
      const filtered = this.availableFilters().filter(row => row.code === this.ratingUnitInput().request.filter);
      if (filtered.length !== 1) {
        this.ratingUnitInput().request.filter = this.availableFilters()[0].code;
      }
      subject.complete();
    } else {
      if (this.ratingUnitInput().request) {
        this.ratingUnitInput().request.filter = undefined!;
      }
      subject.complete();
    }
    return subject.asObservable();
  }

  private loadFilterTypes(): Observable<void> {
    const subject = new Subject<void>();
    this.availableFilters.set([]);
    if (this.isAirHandlerModel()) {
      this.modelService.getEntity(this.engineKey(), 'USFilter').subscribe({
        next: (filterEntity) => {
          for (const allowable of filterEntity.Allowables) {
            if (allowable.IsValid) {
              const filter = new SelectionItem();
              filter.code = allowable.Value;
              filter.description = allowable.Description;
              this.availableFilters.update(current => [...current, filter]);
            }
          }
          subject.complete();
        }, error: (error) => subject.error(error)
      });
    } else {
      subject.complete();
    }
    return subject.asObservable();
  }

  performanceInputChanged(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.updateUnitRating().subscribe({
      next: () => this.isBusy.set(false),
      error: (error) => this.handleError(error),
      complete: () => this.isBusy.set(false)
    });
  }

  selectedMotorTypeChanged(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.handleSelectedMotorTypeChange().subscribe({
      complete: () => {
        this.updateUnitRating().subscribe({
          next: () => this.isBusy.set(false),
          error: (error) => this.handleError(error),
          complete: () => this.isBusy.set(false)
        });
      }, error: (error) => this.handleError(error)
    });
  }

  private handleSelectedMotorTypeChange(): Observable<void> {
    const subject = new Subject<void>();
    if ((this.availableMotorTypes()?.length || 0) > 0) {
      const filtered: MotorType[] = this.availableMotorTypes().filter(motorType => motorType.guruModelValue === this.ratingUnitInput().motorTypeCode);
      if (filtered.length !== 1) {
        this.ratingUnitInput().motorTypeCode = undefined!;
        this.ratingUnitInput().request.motorType = undefined!;
        subject.complete();
      } else {
        this.modelService.setValue('USMotorType', this.engineKey(), this.ratingUnitInput().motorTypeCode).subscribe({
          next: () => {
            this.modelService.isComplete(this.engineKey()).subscribe({
              next: () => {
                this.modelService.getCharacteristicValue(this.engineKey(), 'USMotorType', 'Code').subscribe({
                  next: (code) => {
                    this.ratingUnitInput().request.motorType = code;
                    subject.complete();
                  }, error: (error) => subject.error(error)
                });
              }, error: (error) => subject.error(error)
            });
          }, error: (error) => subject.error(error)
        });
      }
    } else {
      this.ratingUnitInput().motorTypeCode = undefined!;
      this.ratingUnitInput().request.motorType = undefined!;
      subject.complete();
    }
    return subject.asObservable();
  }

  selectedSizeChanged(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.handleSizeChange().subscribe({
      complete: () => this.isBusy.set(false),
      error: (error) => this.handleError(error)
    });
  }

  private handleSizeChange(): Observable<void> {
    const subject: Subject<void> = new Subject<void>();
    if (this.ratingUnitInput().sizeCode && this.availableSizes()) {
      const filtered = this.availableSizes().filter(size => size.sizeCode === this.ratingUnitInput().sizeCode);
      if (filtered.length === 1) {
        if (this.isAirHandlerModel() && this.initialLoadComplete()) {
          this.ratingUnitInput().request.airflow = filtered[0].nominalCfm;
        }
        this.modelService.setValue('USSize', this.engineKey(), this.ratingUnitInput().sizeCode).subscribe({
          complete: () => {
            this.ratingUnitInput().request.size = this.ratingUnitInput().sizeCode;
            this.updateUnitRating().subscribe({
              next: () => subject.complete(),
              complete: () => subject.complete(),
              error: (error) => subject.error(error)
            });
          }, error: (error) => subject.error(error)
        });
      } else {
        this.ratingUnitInput().sizeCode = undefined!;
        subject.complete();
      }
    } else {
      this.ratingUnitInput().sizeCode = undefined!;
      subject.complete();
    }
    return subject.asObservable();
  }

  private setResponseValues(response: SureFlowRatingUnitResponse): void {
    const responseId = this.ratingUnitInput()?.response?.id || 0;
    const circuitResponseId = this.ratingUnitInput()?.response?.circuitResponse?.id || 0;
    const alternateCircuitId = this.ratingUnitInput()?.response?.alternateCircuit?.id || 0;
    const primaryCoilId = this.ratingUnitInput()?.response?.primaryCoilResponse?.id || 0;
    const secondaryCoilId = this.ratingUnitInput()?.response?.secondaryCoilResponse?.id || 0;
    const electricHeatId = this.ratingUnitInput()?.response?.electricHeatResponse?.id || 0;
    this.ratingUnitInput().response = response;
    if (responseId > 0) {
      this.ratingUnitInput().response.id = responseId;
    }
    if (circuitResponseId > 0) {
      this.ratingUnitInput().response.circuitResponse.id = circuitResponseId;
    }
    if (alternateCircuitId > 0) {
      this.ratingUnitInput().response.alternateCircuit.id = alternateCircuitId;
    }
    if (primaryCoilId > 0) {
      this.ratingUnitInput().response.primaryCoilResponse.id = primaryCoilId;
    }
    if (secondaryCoilId > 0) {
      this.ratingUnitInput().response.secondaryCoilResponse.id = secondaryCoilId;
    }
    if (electricHeatId > 0) {
      this.ratingUnitInput().response.electricHeatResponse.id = electricHeatId;
    }
  }
}
